import { Injectable } from '@angular/core';
import { Observable, pipe, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { User } from '@app/models/users/users.model';
import { Device } from '@app/models/devices/devices.model';
import { Notification } from '@app/models/notifications/notifications.model';
import { Location } from '@app/models/locations/locations.model';
import { Fetch } from '@app/models/fetch';
import { AuthState, UpdateUser } from '@app/auth/auth.state';
import { Store } from '@ngxs/store';
import { BatteryOrder } from '@app/models/battery/battery-order.model';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    static self: UserService;

    constructor(private fetch: Fetch, private authState: AuthState, private store: Store) {}

    update(id, body) {
        return this.fetch.patch<User>('//users', id, body).pipe(
            switchMap(() => this.authState.getUser()),
            switchMap((currUser) => {
                if (id === currUser.id) {
                    return this.get().pipe(switchMap((newUser) => this.store.dispatch(new UpdateUser(newUser))));
                }
                return of(currUser);
            })
        );
    }

    changePassword(id, body) {
        return this.fetch.patch<User>(`//users/${id}/password`, '', body);
    }

    changeEmail(id: number | string, body: { email_address: string; password: string }) {
        return this.fetch.patch<User>(`//users/${id}/email`, '', body).pipe(
            switchMap(() => this.authState.getUser()),
            switchMap((currUser) => {
                if (id === currUser.id) {
                    return this.get().pipe(switchMap((newUser) => this.store.dispatch(new UpdateUser(newUser))));
                }
                return of(currUser);
            })
        );
    }

    get() {
        return this.fetch.getOne<User>('//users', this.authState.userID()).model(User);
    }

    joinDevices(devices: Device[]): Observable<any> {
        return this.get().pipe(
            map((user) => {
                for (let i = 0; i < devices.length; i++) {
                    if (devices[i].user_id === user.id) {
                        devices[i].user = user;
                    }
                }
                return devices;
            })
        );
    }

    joinDevicesPipe() {
        return pipe(
            switchMap((devices: Device[]) => {
                if (devices.length === 0) {
                    return of(devices);
                }
                return this.joinDevices(devices);
            })
        );
    }

    joinNotifications(notifs: Notification[]): Observable<Notification[]> {
        return this.get().pipe(
            map((user) => {
                for (let i = 0; i < notifs.length; i++) {
                    if (notifs[i].user_id === user.id) {
                        notifs[i].user = user;
                    }
                }
                return notifs;
            })
        );
    }

    joinNotificationsPipe() {
        return pipe(
            switchMap((notifications: Notification[]) => {
                if (notifications.length === 0) {
                    return of(notifications);
                }
                return this.joinNotifications(notifications);
            })
        );
    }

    joinLocation(location: Location): Observable<Location> {
        return this.get().pipe(
            map((user) => {
                location.user = user;
                return location;
            })
        );
    }

    joinLocations(locs: Location[]) {
        return this.get().pipe(
            map((user) => {
                for (let i = 0; i < locs.length; i++) {
                    if (locs[i].user_id === user.id) {
                        locs[i].user = user;
                    }
                }
                return locs;
            })
        );
    }

    joinLocationPipe() {
        return pipe(switchMap((loc: Location) => this.joinLocation(loc)));
    }

    joinLocationsPipe() {
        return pipe(switchMap((locations: Location[]) => this.joinLocations(locations)));
    }

    getUserEntitlement() {
        return this.get().pipe(map((user) => user.plan.entitlement.toLowerCase()));
    }

    getUserEntitlementObject() {
        return this.get().pipe(map((user) => user.plan));
    }

    // route for obtaining the purchase link for new batteries
    getPurchaseOptions(deviceID: string) {
        return this.fetch
            .get(`//users/${this.authState.userID()}/devices/${deviceID}/purchase-options`)
            .model(BatteryOrder);
    }
}
